import {
  type Board,
  type BoardAspect,
  type BoardGenerator,
  type BoardImageNode,
  IMAGE_NODE_H,
  IMAGE_NODE_W,
  NODE_GAP_X,
  NODE_GAP_Y,
  connect,
  connectImagesTo,
  createImageNode,
  createVideoNode,
  isBoardAspect,
  isImage,
  isVideo,
  nodeSize,
} from './canvasBoard';
import type { TemplateLockFlags } from './exploreMatch';

/** Normalize for comparison: strip query/hash and prefer the media path tail. */
function mediaPathKey(url: string): string {
  const raw = url.trim();
  if (!raw) return '';
  try {
    const parsed = new URL(raw, 'https://local.invalid');
    const path = decodeURIComponent(parsed.pathname).replace(/\/+$/, '').toLowerCase();
    const media = path.indexOf('/media/');
    if (media >= 0) return path.slice(media);
    const file = path.split('/').filter(Boolean).slice(-2).join('/');
    return file || path;
  } catch {
    return raw.split('?')[0].split('#')[0].toLowerCase();
  }
}

export function sameMediaUrl(a: string, b: string): boolean {
  if (a === b) return true;
  const left = mediaPathKey(a);
  const right = mediaPathKey(b);
  if (left && right && left === right) return true;
  try {
    const u1 = new URL(a);
    const u2 = new URL(b);
    return u1.pathname.length > 1 && u1.pathname === u2.pathname;
  } catch {
    return false;
  }
}

export type TemplateGeneratorInput = {
  title: string;
  aspectRatio?: string;
  imageUrls: string[];
  labels?: string[];
  /** Intermediate images from an Explore template with stages — shown on their own row. */
  stageUrls?: string[];
  /**
   * When false (template `stages_include_user_images`), only the template's
   * start/end photos (imageUrls) plus stages feed the video — intermediate
   * board photos stay unlinked. When true, every imageUrl is linked.
   */
  includeUserImages?: boolean;
  /** Placeholder image cards (loading) while Explore stages are still rendering. */
  pendingStageCount?: number;
  model: {
    videoModelId: string;
    resolutionId: string;
    durationSeconds: number;
    generateAudio: boolean;
  };
  locks?: TemplateLockFlags;
  /** Empty while stages are still rendering on the canvas handoff. */
  remixJobId?: string;
  progress?: string;
};

/**
 * Drops a prompt-less generator on the board, links the photos into it, and marks the remix job as running.
 * Images already on the board are reused. The template prompt stays off the card.
 * When the model used stages, those images sit on a second row and feed the video.
 */
export function placeTemplateGenerator(
  board: Board,
  input: TemplateGeneratorInput,
): { board: Board; videoId: string } | { error: string } {
  const stageUrls: string[] = [];
  (input.stageUrls || []).forEach((raw) => {
    const url = raw.trim();
    if (!url || stageUrls.some((kept) => sameMediaUrl(kept, url))) return;
    stageUrls.push(url);
  });
  const hasStages = stageUrls.length > 0 || (Number(input.pendingStageCount) || 0) > 0;
  // Always place/link the template's imageUrls (start/end). With includeUserImages
  // false those are already just the slot picks — never leave them floating unlinked.
  const linkAllUserPhotos = input.includeUserImages !== false || !hasStages;
  const urls: string[] = [];
  const labels: string[] = [];
  const rawUser = (input.imageUrls || [])
    .map((raw, index) => ({ url: raw.trim(), label: input.labels?.[index] || '' }))
    .filter((row) => row.url);
  const userSource = linkAllUserPhotos
    ? rawUser
    : (() => {
      if (!rawUser.length) return [] as typeof rawUser;
      if (rawUser.length === 1) return [rawUser[0]];
      const first = rawUser[0];
      const last = rawUser[rawUser.length - 1];
      return sameMediaUrl(first.url, last.url) ? [first] : [first, last];
    })();
  userSource.forEach((row, index) => {
    if (urls.some((kept) => sameMediaUrl(kept, row.url))) return;
    if (stageUrls.some((kept) => sameMediaUrl(kept, row.url))) return;
    urls.push(row.url);
    labels.push(row.label || `Imagem ${index + 1}`);
  });

  const nodes = board.nodes.map((node) => ({ ...node }));
  const imageIds: string[] = [];
  const created: BoardImageNode[] = [];
  urls.forEach((url, index) => {
    const found = nodes.find((node) => isImage(node) && node.phase.imageUrl && sameMediaUrl(node.phase.imageUrl, url));
    if (found) {
      imageIds.push(found.id);
      return;
    }
    const node = createImageNode(url, 0, 0, labels[index] || `Imagem ${index + 1}`);
    created.push(node);
    nodes.push(node);
    imageIds.push(node.id);
  });

  const known = nodes.filter((node) => imageIds.includes(node.id) && !created.some((item) => item.id === node.id));
  const boardImages = nodes.filter(isImage);
  const anchorY = known.length
    ? Math.min(...known.map((node) => node.y))
    : boardImages.length
      ? Math.min(...boardImages.map((node) => node.y))
      : nodes.length
        ? Math.max(...nodes.map((node) => node.y + nodeSize(node).h)) + NODE_GAP_Y
        : 80;
  const left = known.length
    ? Math.min(...known.map((node) => node.x))
    : boardImages.length
      ? Math.min(...boardImages.map((node) => node.x))
      : 80;
  created.forEach((node, index) => {
    node.x = known.length || boardImages.length
      ? left - (created.length - index) * (IMAGE_NODE_W + NODE_GAP_X)
      : 80 + index * (IMAGE_NODE_W + NODE_GAP_X);
    node.y = anchorY;
  });

  const placed = imageIds
    .map((id) => nodes.find((node) => node.id === id))
    .filter((node): node is BoardImageNode => isImage(node));
  const layoutBasis = placed.length
    ? placed
    : boardImages;
  const maxRight = layoutBasis.length ? Math.max(...layoutBasis.map((node) => node.x + nodeSize(node).w)) : 80;
  const videoY = layoutBasis.length ? Math.min(...layoutBasis.map((node) => node.y)) : anchorY;
  // Start/end = user slot photos. Stages feed the video as attachments/chain refs, not as start/end.
  const startEndRefs = urls.map((url, index) => ({ url, label: labels[index] || `Imagem ${index + 1}` }));
  const stageRefs = stageUrls.map((url, index) => ({ url, label: `Stage ${index + 1}` }));
  const generator: BoardGenerator = {
    hidePrompt: true,
    ...(input.locks ? { locks: input.locks } : {}),
    ...(startEndRefs[0] ? { startImage: startEndRefs[0] } : {}),
    ...(startEndRefs.length > 1
      ? { endImage: startEndRefs[startEndRefs.length - 1] }
      : {}),
    ...(stageRefs.length
      ? { attachments: stageRefs }
      : startEndRefs.length > 2
      ? { attachments: startEndRefs.slice(1, -1) }
      : {}),
  };
  const video = {
    ...createVideoNode(maxRight + NODE_GAP_X, videoY),
    title: input.title.slice(0, 80) || 'Modelo',
    imageMode: 'reference' as const,
    videoModelId: input.model.videoModelId,
    resolutionId: input.model.resolutionId,
    totalDurationSeconds: input.model.durationSeconds,
    generateAudio: input.model.generateAudio,
    generator,
  };

  const stageIds: string[] = [];
  const userBottom = layoutBasis.length
    ? Math.max(...layoutBasis.map((node) => node.y + nodeSize(node).h))
    : anchorY + IMAGE_NODE_H;
  const stageY = userBottom + NODE_GAP_Y;
  const stageLeft = layoutBasis.length ? Math.min(...layoutBasis.map((node) => node.x)) : 80;
  if (stageUrls.length) {
    stageUrls.forEach((url, index) => {
      const found = nodes.find((node) => isImage(node) && node.phase.imageUrl && sameMediaUrl(node.phase.imageUrl, url));
      if (found) {
        stageIds.push(found.id);
        return;
      }
      const node = createImageNode(url, stageLeft + index * (IMAGE_NODE_W + NODE_GAP_X), stageY, `Stage ${index + 1}`);
      nodes.push(node);
      stageIds.push(node.id);
    });
  } else {
    const pending = Math.max(0, Math.min(12, Math.round(Number(input.pendingStageCount) || 0)));
    for (let index = 0; index < pending; index += 1) {
      const node = createImageNode('', stageLeft + index * (IMAGE_NODE_W + NODE_GAP_X), stageY, `Stage ${index + 1}`);
      node.phase = {
        ...node.phase,
        imageUrl: undefined,
        status: 'generating',
        error: `Gerando stage ${index + 1}…`,
      };
      nodes.push(node);
      stageIds.push(node.id);
    }
  }

  const aspect = isBoardAspect(input.aspectRatio) ? input.aspectRatio : board.aspectRatio;
  let next: Board = {
    ...board,
    nodes: [...nodes, video],
    ...(input.locks?.aspect && isBoardAspect(input.aspectRatio)
      ? { aspectRatio: input.aspectRatio as BoardAspect, aspectChosen: true }
      : { aspectRatio: aspect }),
  };
  // Stages feed the video as the chronological inputs (solid arrows).
  // Start/end user photos are identity refs only (dashed) when the template
  // does not include user images in the video sequence.
  const stageOnlyUserRefs = hasStages && input.includeUserImages === false;
  const flowIds = stageOnlyUserRefs ? [...stageIds] : [...imageIds, ...stageIds];
  if (flowIds.length) {
    const linked = connectImagesTo(next, flowIds, video.id, 'given');
    if ('error' in linked) return linked;
    next = linked.board;
  }
  if (stageOnlyUserRefs && imageIds.length) {
    for (const id of imageIds) {
      next = connect(next, id, video.id, 'ref');
    }
  }
  next = {
    ...next,
    nodes: next.nodes.map((node) => (node.id === video.id && isVideo(node)
      ? {
        ...node,
        status: 'generating' as const,
        progress: input.progress || (input.remixJobId ? 'Enviando…' : 'Preparando…'),
        ...(input.remixJobId ? { remixJobId: input.remixJobId, templateJobId: input.remixJobId } : {}),
        totalDurationSeconds: input.model.durationSeconds,
        videoModelId: input.model.videoModelId,
        resolutionId: input.model.resolutionId,
        generateAudio: input.model.generateAudio,
        imageMode: 'reference' as const,
        title: video.title,
        generator,
      }
      : node)),
  };
  return { board: next, videoId: video.id };
}

function stagePlaceholderIndex(label: string | undefined): number {
  const match = /^Stage\s+(\d+)$/i.exec(String(label || '').trim());
  return match ? Number(match[1]) : 0;
}

/**
 * Paint stage cards as each URL arrives. Keeps remaining placeholders generating.
 * Does not drop unfinished slots (unlike the final attach).
 */
export function fillTemplateStagePlaceholders(
  board: Board,
  videoId: string,
  stageUrls: string[],
): { board: Board } | { error: string } {
  const target = board.nodes.find((node) => node.id === videoId);
  if (!isVideo(target)) return { error: 'Gerador do modelo não encontrado no fluxo.' };
  const nodes = board.nodes.map((node) => ({ ...node }));
  // Keep index alignment (null/'' = still generating). Do not compact the list.
  const urls = stageUrls.map((raw) => String(raw || '').trim());
  if (!urls.some(Boolean)) return { board };
  const placeholders = nodes
    .filter((node): node is BoardImageNode => (
      isImage(node)
      && stagePlaceholderIndex(node.phase.label) > 0
    ))
    .sort((a, b) => stagePlaceholderIndex(a.phase.label) - stagePlaceholderIndex(b.phase.label));
  if (!placeholders.length) return { board };
  let changed = false;
  urls.forEach((url, index) => {
    if (!url) return;
    const placeholder = placeholders[index];
    if (!placeholder) return;
    if (placeholder.phase.imageUrl && sameMediaUrl(placeholder.phase.imageUrl, url) && placeholder.phase.status === 'ready') {
      return;
    }
    const idx = nodes.findIndex((node) => node.id === placeholder.id);
    if (idx < 0 || !isImage(nodes[idx])) return;
    const cur = nodes[idx] as BoardImageNode;
    nodes[idx] = {
      ...cur,
      phase: {
        ...cur.phase,
        imageUrl: url,
        status: 'ready',
        error: undefined,
        label: cur.phase.label || `Stage ${index + 1}`,
      },
    };
    changed = true;
  });
  if (!changed) return { board };
  const readyCount = urls.filter(Boolean).length;
  const total = placeholders.length;
  const attachments = placeholders.map((placeholder, index) => {
    const node = nodes.find((row) => row.id === placeholder.id);
    const url = (isImage(node) && node.phase.imageUrl) || urls[index] || '';
    return { url, label: `Stage ${index + 1}` };
  }).filter((row) => row.url);
  return {
    board: {
      ...board,
      nodes: nodes.map((node) => (node.id === videoId && isVideo(node)
        ? {
          ...node,
          status: 'generating' as const,
          progress: `Stages ${readyCount}/${total}…`,
          error: undefined,
          generator: {
            ...(node.generator || { hidePrompt: true }),
            attachments,
          },
        }
        : node)),
    },
  };
}

/**
 * After Explore stages finish: fill the loading stage cards (or add them), keep links, attach the remix job.
 */
export function attachTemplateStages(
  board: Board,
  videoId: string,
  stageUrls: string[],
  remixJobId: string,
): { board: Board } | { error: string } {
  const target = board.nodes.find((node) => node.id === videoId);
  if (!isVideo(target)) return { error: 'Gerador do modelo não encontrado no fluxo.' };
  const nodes = board.nodes.map((node) => ({ ...node }));
  const urls: string[] = [];
  stageUrls.forEach((raw) => {
    const url = raw.trim();
    if (!url || urls.some((kept) => sameMediaUrl(kept, url))) return;
    urls.push(url);
  });
  const placeholders = nodes
    .filter((node): node is BoardImageNode => (
      isImage(node)
      && stagePlaceholderIndex(node.phase.label) > 0
      && (!node.phase.imageUrl || node.phase.status === 'generating' || node.phase.status === 'pending')
    ))
    .sort((a, b) => stagePlaceholderIndex(a.phase.label) - stagePlaceholderIndex(b.phase.label));
  const stageIds: string[] = [];
  const videoBottom = target.y + nodeSize(target).h;
  const stageY = Math.max(
    videoBottom + NODE_GAP_Y,
    placeholders.length ? Math.min(...placeholders.map((node) => node.y)) : videoBottom + NODE_GAP_Y,
  );
  const stageLeft = placeholders.length
    ? Math.min(...placeholders.map((node) => node.x))
    : target.x;
  urls.forEach((url, index) => {
    const byUrl = nodes.find((node) => isImage(node) && node.phase.imageUrl && sameMediaUrl(node.phase.imageUrl, url));
    if (byUrl && isImage(byUrl)) {
      stageIds.push(byUrl.id);
      return;
    }
    const placeholder = placeholders[index];
    if (placeholder) {
      const idx = nodes.findIndex((node) => node.id === placeholder.id);
      if (idx >= 0 && isImage(nodes[idx])) {
        const cur = nodes[idx] as BoardImageNode;
        nodes[idx] = {
          ...cur,
          phase: {
            ...cur.phase,
            imageUrl: url,
            status: 'ready',
            error: undefined,
            label: cur.phase.label || `Stage ${index + 1}`,
          },
        };
        stageIds.push(cur.id);
        return;
      }
    }
    const node = createImageNode(url, stageLeft + index * (IMAGE_NODE_W + NODE_GAP_X), stageY, `Stage ${index + 1}`);
    nodes.push(node);
    stageIds.push(node.id);
  });
  // Drop leftover empty stage placeholders that never received a URL.
  const keep = new Set(stageIds);
  const cleaned = nodes.filter((node) => {
    if (!isImage(node)) return true;
    if (keep.has(node.id)) return true;
    if (stagePlaceholderIndex(node.phase.label) <= 0) return true;
    return Boolean(node.phase.imageUrl) && node.phase.status !== 'generating';
  });
  let next: Board = { ...board, nodes: cleaned };
  const stageAttachments = urls.map((url, index) => ({ url, label: `Stage ${index + 1}` }));
  const markVideo = (current: Board): Board => ({
    ...current,
    nodes: current.nodes.map((node) => (node.id === videoId && isVideo(node)
      ? {
        ...node,
        status: 'generating' as const,
        progress: 'Enviando…',
        remixJobId,
        templateJobId: remixJobId,
        error: undefined,
        generator: {
          ...(node.generator || { hidePrompt: true }),
          // Chronological stage order for the generator reference rail.
          attachments: stageAttachments,
        },
      }
      : node)),
  });
  if (stageIds.length) {
    // connectImagesTo refuses a generating target — park it idle for the rewire, then restore.
    const idleTarget: Board = {
      ...next,
      nodes: next.nodes.map((node) => (node.id === videoId && isVideo(node)
        ? { ...node, status: 'idle' as const, progress: undefined }
        : node)),
    };
    // Solid flow = stages (+ any non-ref user photos). Keep dashed start/end refs.
    const refIds = new Set(
      idleTarget.edges
        .filter((edge) => edge.to === videoId && edge.role === 'ref')
        .map((edge) => edge.from),
    );
    const solidUserIds = idleTarget.edges
      .filter((edge) => edge.to === videoId && edge.role !== 'ref')
      .map((edge) => edge.from)
      .filter((id) => {
        const node = idleTarget.nodes.find((row) => row.id === id);
        return isImage(node) && stagePlaceholderIndex(node.phase.label) <= 0 && !refIds.has(id);
      });
    const linked = connectImagesTo(idleTarget, [...solidUserIds, ...stageIds], videoId, 'given');
    if ('error' in linked) return { board: markVideo(next) };
    next = linked.board;
    // connectImagesTo keeps existing ref edges; re-assert start/end refs just in case.
    Array.from(refIds).forEach((id) => {
      next = connect(next, id, videoId, 'ref');
    });
  }
  return { board: markVideo(next) };
}
